import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { DatetimeAdapter } from '@ng-matero/extensions/core';
import { MtxDatetimepickerMode, MtxDatetimepickerType } from '@ng-matero/extensions/datetimepicker';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { type Moment } from 'moment-timezone';

@Component({
    selector: 'st-date-picker',
    templateUrl: './date-picker.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./date-picker.scss'],
    host: {
        class: 'st-date-picker',
    },
})
export class DatePickerComponent extends EnpsControlComponent<DateTime> {
    dateTimeAdapter = inject(DatetimeAdapter<Moment>);
    min = input<DateTime>(new DateTime());

    internalValue = signal<DateTime>(new DateTime());

    internalFormControl: FormControl<Moment> = new FormControl(null);
    type: MtxDatetimepickerType = 'date';
    mode: MtxDatetimepickerMode = 'auto';
    touchUi = false;

    clearLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebDatePickerClearLabel));
    cancelLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebCancelButtonLabel));
    applyLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebApplyButtonLabel));

    minDate = computed(() => {
        return this.min()?.moment || null;
    });

    constructor() {
        super();
        effect(
            () => {
                const val = this.value();
                this.internalValue.set(val);
                if (this.dateLocale()) {
                    this.dateTimeAdapter.setLocale(this.dateLocale());
                }
            },
            { allowSignalWrites: true }
        );
    }

    dateChange(value: Moment) {
        if (value) {
            const newDate = this.internalValue().get() || new Date();
            newDate?.setFullYear(value.toDate().getFullYear());
            newDate?.setMonth(value.toDate().getMonth(), value.toDate().getDate());
            const newDateTime = new DateTime(newDate);
            this._calculateNgTimeOffset(newDate);
            this.changed.emit(newDateTime);
        } else {
            this.internalValue.update(val => {
                val.set(null);
                return val;
            });
            this.changed.emit(this.internalValue());
        }
    }

    //TODO: Implement clearDate after we send a PR with the changes to the DateTimePicker footer
    // public clearDate() {
    //     this.value.set(null);
    //     this.internalFormControl.setValue(null);
    //     this.changed.emit(this.value);
    // }

    dateValue = computed(() => {
        if (this.internalValue()?.get()?.getFullYear() === 1800) {
            return null;
        }
        return this.internalValue()?.moment || null;
    });

    _syncValueIfNecessary() {
        this.internalFormControl.setValue(this.dateValue());
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }

    private _calculateNgTimeOffset(date: Date) {
        const offset = this.visualStateService.ConfigSettings.NGTimeOffset;
        if (offset) {
            const chunks = offset.split(':');
            const hours = parseInt(chunks[0]);
            const minutes = parseInt(chunks[1]);
            date.setUTCHours(hours);
            date.setUTCMinutes(minutes);
            date.setUTCSeconds(0);
            date.setUTCMilliseconds(0);
        }
    }
}
